// src/components/SceneLayers/Connectors/Connector.tsx
import { useMemo, useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { UNPROJECTED_TILE_SIZE, DEFAULT_COLOR } from 'src/config';
import { getColorVariant } from 'src/utils';
import { Svg } from 'src/components/Svg/Svg';
import { useIsoProjection } from 'src/hooks/useIsoProjection';
import { useConnector } from 'src/hooks/useConnector';
import { useScene } from 'src/hooks/useScene';
import { useColor } from 'src/hooks/useColor';
import { useConnectorFlow } from 'src/hooks/useConnectorFlow';
import { useAnimationStore } from '@/stores/animationStore';
import { calculateFlowParticles } from '@/features/animations/flowParticles';

interface Props {
  connector: ReturnType<typeof useScene>['connectors'][0];
  isSelected?: boolean;
}

type SceneConnector = ReturnType<typeof useScene>['connectors'][number];

interface FlowParticlesProps {
  connector: SceneConnector;
  drawOffset: { x: number; y: number };
  color: string;
}

// ========== Flow Particles Component ==========

export const FlowParticles = ({
  connector,
  drawOffset,
  color
}: FlowParticlesProps) => {
  const exportTimeMs = useAnimationStore((state) => state.exportTimeMs);
  const [liveTimeMs, setLiveTimeMs] = useState(() => Date.now());

  useEffect(() => {
    if (!connector.flowEnabled || exportTimeMs !== null) return;

    let rafId: number;
    const updateParticles = () => {
      setLiveTimeMs(Date.now());
      rafId = requestAnimationFrame(updateParticles);
    };

    rafId = requestAnimationFrame(updateParticles);

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [connector.flowEnabled, exportTimeMs]);

  const particles = useMemo(
    () =>
      calculateFlowParticles(
        { ...connector, color: color || connector.color },
        exportTimeMs ?? liveTimeMs
      ),
    [connector, color, exportTimeMs, liveTimeMs]
  );

  if (!connector.flowEnabled || particles.length === 0) {
    return null;
  }

  return (
    <g>
      {particles.map((particle) => {
        const screenX = particle.x * UNPROJECTED_TILE_SIZE + drawOffset.x;
        const screenY = particle.y * UNPROJECTED_TILE_SIZE + drawOffset.y;

        return (
          <circle
            key={particle.id}
            cx={screenX}
            cy={screenY}
            r={particle.size / 2}
            fill={particle.color}
            opacity={particle.opacity}
            style={{ pointerEvents: 'none' }}
          />
        );
      })}
    </g>
  );
};

export const Connector = ({ connector: _connector, isSelected }: Props) => {
  const color = useColor(_connector?.color);
  const colorValue =
    typeof color === 'string' ? color : color?.value || DEFAULT_COLOR.value;
  // const { currentView } = useScene();
  const connector = useConnector(_connector.id);
  // Start flow animation (this manages the animation state)
  useConnectorFlow(_connector);

  const { css, pxSize } = useIsoProjection({
    ...connector.path.rectangle
  });

  const drawOffset = useMemo(() => {
    return {
      x: UNPROJECTED_TILE_SIZE / 2,
      y: UNPROJECTED_TILE_SIZE / 2
    };
  }, []);

  const pathString = useMemo(() => {
    return connector.path.tiles.reduce((acc, tile) => {
      return `${acc} ${tile.x * UNPROJECTED_TILE_SIZE + drawOffset.x},${
        tile.y * UNPROJECTED_TILE_SIZE + drawOffset.y
      }`;
    }, '');
  }, [connector.path.tiles, drawOffset]);

  const connectorWidthPx = useMemo(() => {
    return (UNPROJECTED_TILE_SIZE / 100) * (connector.width || 4);
  }, [connector.width]);

  const strokeDashArray = useMemo(() => {
    const style = connector.style || 'SOLID';
    switch (style) {
      case 'DASHED':
        return `${connectorWidthPx * 2}, ${connectorWidthPx * 2}`;
      case 'DOTTED':
        return `0, ${connectorWidthPx * 1.8}`;
      case 'SOLID':
      default:
        return 'none';
    }
  }, [connector.style, connectorWidthPx]);

  const gradientId = `connector-gradient-${connector.id}`;

  const gradientVector = useMemo(() => {
    if (connector.path.tiles.length < 2) {
      return { x1: 0, y1: 0, x2: 1, y2: 0 };
    }

    const firstTile = connector.path.tiles[0];
    const lastTile = connector.path.tiles[connector.path.tiles.length - 1];

    const x1 = firstTile.x * UNPROJECTED_TILE_SIZE + drawOffset.x;
    const y1 = firstTile.y * UNPROJECTED_TILE_SIZE + drawOffset.y;
    const x2 = lastTile.x * UNPROJECTED_TILE_SIZE + drawOffset.x;
    const y2 = lastTile.y * UNPROJECTED_TILE_SIZE + drawOffset.y;

    return { x1, y1, x2, y2 };
  }, [connector.path.tiles, drawOffset]);

  const fadeEnabled = connector.fadeEnds === true;

  if (!connector.path.tiles) {
    console.warn(`Connector data missing for ID: ${_connector.id}`);
    return null;
  }

  return (
    <Box style={{ ...css, pointerEvents: 'none' }}>
      <Svg
        style={{
          transform: 'scale(-1, 1)',
          // Connector SVG rectangles frequently overlap. Only the explicit
          // transparent stroke below may own pointer input, otherwise a later
          // connector's empty bounding box can block the visible line.
          pointerEvents: 'none',
          overflow: 'visible'
        }}
        viewboxSize={pxSize}
      >
        {fadeEnabled && (
          <defs>
            <linearGradient
              id={gradientId}
              x1={gradientVector.x1}
              y1={gradientVector.y1}
              x2={gradientVector.x2}
              y2={gradientVector.y2}
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%" stopColor="white" stopOpacity="0.3" />
              <stop offset="15%" stopColor="white" stopOpacity="1" />
              <stop offset="85%" stopColor="white" stopOpacity="1" />
              <stop offset="100%" stopColor="white" stopOpacity="0.3" />
            </linearGradient>
            <linearGradient
              id={`${gradientId}-main`}
              x1={gradientVector.x1}
              y1={gradientVector.y1}
              x2={gradientVector.x2}
              y2={gradientVector.y2}
              gradientUnits="userSpaceOnUse"
            >
              <stop
                offset="0%"
                stopColor={getColorVariant(colorValue, 'dark', { grade: 1 })}
                stopOpacity="0.3"
              />
              <stop
                offset="15%"
                stopColor={getColorVariant(colorValue, 'dark', { grade: 1 })}
                stopOpacity="1"
              />
              <stop
                offset="85%"
                stopColor={getColorVariant(colorValue, 'dark', { grade: 1 })}
                stopOpacity="1"
              />
              <stop
                offset="100%"
                stopColor={getColorVariant(colorValue, 'dark', { grade: 1 })}
                stopOpacity="0.3"
              />
            </linearGradient>
          </defs>
        )}

        <g
          className="connector-hover-target"
          data-scene-item-type="CONNECTOR"
          data-scene-item-id={connector.id}
          style={{ cursor: 'grab' }}
        >
          <style>{`
            .connector-hover-target:hover .connector-hover-highlight {
              opacity: 0.4;
            }
          `}</style>
          {/* Invisible hit area for easier clicking */}
          <polyline
            points={pathString}
            stroke="transparent"
            strokeWidth={20}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            style={{ pointerEvents: 'stroke' }}
          />

          {/* Selected connector halo. Render behind the main line so the
              connector keeps its own color while selection stays obvious. */}
          {isSelected && (
            <polyline
              points={pathString}
              stroke="#0392ff"
              strokeWidth={Math.max(
                connectorWidthPx + 3,
                connectorWidthPx * 1.5
              )}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeOpacity={0.28}
              fill="none"
              style={{
                pointerEvents: 'none',
                filter: 'drop-shadow(0 0 2px rgba(3, 146, 255, 0.35))'
              }}
            />
          )}

          {/* Main connector line */}
          <polyline
            points={pathString}
            stroke={
              fadeEnabled
                ? `url(#${gradientId}-main)`
                : getColorVariant(colorValue, 'dark', { grade: 1 })
            }
            strokeWidth={connectorWidthPx}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray={strokeDashArray}
            fill="none"
            style={{ pointerEvents: 'none' }}
          />

          {/* ✨ NEW: Hover highlight */}
          {!isSelected && (
            <polyline
              className="connector-hover-highlight"
              points={pathString}
              stroke="#6366f1"
              strokeWidth={connectorWidthPx * 1.6}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeOpacity={1}
              opacity={0}
              fill="none"
              style={{ pointerEvents: 'none' }}
            />
          )}
        </g>

        {/* ========== NEW: Render Flow Particles ========== */}
        <FlowParticles
          connector={connector}
          drawOffset={drawOffset}
          color={colorValue}
        />
        {/* ================================================= */}

      </Svg>
    </Box>
  );
};
